import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { rulesApi } from '../../services/api';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const RequestRuleModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = name.trim().length > 0 && description.trim().length > 0 && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');
    try {
      const payload: Record<string, any> = {
        rule_name: name.trim(),
        description: description.trim(),
      };
      const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);
      if (tagList.length > 0) payload.tags = tagList;

      await rulesApi.requestRule(payload);
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cargo-border">
          <div>
            <h2 className="text-lg font-semibold text-cargo-text">Request New Rule</h2>
            <p className="text-xs text-cargo-muted mt-0.5">Submit a request for a new data quality rule</p>
          </div>
          <button onClick={onClose} className="text-cargo-muted hover:text-cargo-text">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Rule Name */}
          <div>
            <label className="block text-xs font-medium text-cargo-muted mb-1">
              Rule Name <span className="text-cargo-error">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              placeholder="e.g. expect_column_values_to_match_pattern"
              className="w-full px-3 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                         text-cargo-text placeholder-cargo-muted/50 text-sm
                         focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
            />
            <p className="text-xs text-cargo-muted/50 mt-1">{name.length}/255</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-cargo-muted mb-1">
              Description <span className="text-cargo-error">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Describe what this rule should check and when it should be used..."
              className="w-full px-3 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                         text-cargo-text placeholder-cargo-muted/50 text-sm
                         focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none resize-y"
            />
            <p className="text-xs text-cargo-muted/50 mt-1">{description.length}/500</p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-cargo-muted mb-1">Tags</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. validation, pattern, custom (comma-separated)"
              className="w-full px-3 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                         text-cargo-text placeholder-cargo-muted/50 text-sm
                         focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-cargo-error bg-cargo-error/10 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-cargo-muted hover:text-cargo-text transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                         text-sm font-medium px-5 py-2 rounded-lg transition-colors disabled:opacity-30"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestRuleModal;
